// rescontrolcambios.actions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { NavigateFunction } from "react-router-dom";
import { UseMutationsParams, AlertSeverityEnum } from "@/shared/interfaces";
import { erpApi } from "@/shared/axios";
import { toApiError } from "@/shared/utils/general";
import { useUiStore } from "@/store/ui";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { urlBackend } from "@/shared/constants";
import {
  Servidor,
  ServidorForm,
  ServidorParams,
  ServidorPagingResp,
} from "@/erp/controlcambios/servidores/types/servidoresControlCambios.interface";
// import { useFetchUsuariosSistemaPorArreglo } from "../erp/administration/usuarios";


const { get, post, put, remove, patch } = erpApi();
const BASE = `${urlBackend}api/v1/cambios/servidores`;

// listar
export const useFetchGetServidores = (
  params?: ServidorParams,
  enabled = true
) =>
  useQuery({
    queryKey: ["servidores", ...Object.values(params ?? {})],
    queryFn: async () => getServidores(params),
    enabled,
  });

export const getServidores = async (params?: ServidorParams): Promise<ServidorPagingResp> => {
  const q = getUrlParamsFetch(params ?? {});
  return (await get<ServidorPagingResp>(`${BASE}?${q}`, true)) as unknown as ServidorPagingResp;
};

// obtener por id
export const useFetchGetByIdServidor = (id: string) =>
  useQuery({
    queryKey: ["servidor", id],
    queryFn: async () => getByIdServidor(id),
    refetchOnWindowFocus: false,
  });

export const getByIdServidor = async (id: string) =>
  await get<Servidor>(`${BASE}/${id}`, true);

// crear
export const useFetchPostServidor = ({
  returnUrl,
  navigate,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (data: ServidorForm) => postServidor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["servidores"] });
      navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlerta &&
        setAlerta(
          true,
          customSuccessMessage ?? "Servidor creado correctamente",
          AlertSeverityEnum.success
        );
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const postServidor = async (data: ServidorForm) =>
  await post<Servidor>(BASE, data, true);

// actualizar
export const useFetchPutServidor = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: putServidor,
    onSuccess: (_d, variables) => {
      queryClient.invalidateQueries({ queryKey: ["servidores"] });
      queryClient.invalidateQueries({ queryKey: ["servidor", variables.id] });
      setAlerta(true, "Servidor actualizado correctamente", AlertSeverityEnum.success);
      navigate(returnUrl);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const putServidor = async ({
  id,
  data,
}: {
  id: string;
  data: Omit<ServidorForm, "servidorId">;
}) => await put<Servidor>(`${BASE}/${id}`, data, true);

// eliminar
export const useFetchDeleteServidor = () => {
  const queryClient = useQueryClient();
  const setAlerta = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: (id: string) => deleteServidor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["servidores"] });
      setAlerta(true, "Servidor eliminado correctamente", AlertSeverityEnum.success);
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const deleteServidor = async (id: string) =>
  await remove<Servidor>(`${BASE}/${id}`, true);


//activo

export const useFetchPatchEstadoServidor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchEstadoServidor,
    onSuccess: () => {
      toast.success("Estado del servidor actualizado");
      queryClient.invalidateQueries({ queryKey: ["servidores"] });
    },
    onError: (error) => toast.error(toApiError(error).message),
  });
};

export const patchEstadoServidor = async ({ id, estado }: { id: string; estado: boolean }) =>
  await patch(`${BASE}/${id}/estado?estado=${estado}`, undefined, true);






